import React, { useEffect, useState, useRef, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hook";
import { fetchVouchers, type VoucherListParams } from "../../features/voucher/VoucherSlice";
import type { DashboardFiltersState } from "./DashboardFilters";
import type { SortOrder, TimezoneFilter, VoucherSortField } from "./filters/types";
import { DollarSign, Calendar, Clock, Hash, Banknote, AlertCircle, Loader2, CheckCircle, XCircle, Tag, Clock as ClockIcon, Download, Info, Search } from 'lucide-react';
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import { fetchVoucherListApi } from "../../features/voucher/VoucherService";
import { useLobTimezone } from "../../hooks/useLobTimezone";

interface VouchersSectionProps {
  dateRange: { startDate: Date; endDate: Date }[];
  page: number;
  limit?: number;
  onPageChange?: (page: number) => void;
  dashboardFilters: DashboardFiltersState;
}

const statusColors: Record<string, string> = {
  activated: "bg-green-100 text-green-700",
  issued: "bg-blue-100 text-blue-700",
  expired: "bg-red-100 text-red-700",
  pending: "bg-yellow-100 text-yellow-700",
  redeemed: "bg-blue-100 text-blue-700",
  failed: "bg-red-100 text-red-700",
  used: "bg-purple-100 text-purple-700",
  revoked: "bg-gray-100 text-gray-700",
  exhausted: "bg-orange-100 text-orange-700",
};

const reconStatusTextColors: Record<string, string> = {
  pending: "text-orange-600",
  failed: "text-red-600",
  success: "text-green-600",
  processing: "text-blue-600",
  unknown: "text-gray-600",
};

const sortFieldOptions: { value: VoucherSortField; label: string }[] = [
  { value: "createdAt", label: "Created date" },
  { value: "voucherCreateDate", label: "Activation date" },
  { value: "expiryDate", label: "Expiry date" },
  { value: "amount", label: "Amount" },
  { value: "saralId", label: "SARAL ID" },
  { value: "state", label: "Status" },
];

const formatDateInTimezone = (dateStr: string | null | undefined, timezone: TimezoneFilter, lobIana: string) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return "";
  const timeZone = timezone === "UTC" ? "UTC" : lobIana;
  return date.toLocaleDateString("en-CA", { timeZone });
};

const getReconIcon = (status: string) => {
  const lowerStatus = status?.toLowerCase();
  switch (lowerStatus) {
    case 'pending':
      return <ClockIcon className="w-3 h-3 text-orange-600" />;
    case 'failed':
      return <XCircle className="w-3 h-3 text-red-600" />;
    case 'success':
      return <CheckCircle className="w-3 h-3 text-green-600" />;
    case 'processing':
      return <Loader2 className="w-3 h-3 text-blue-600 animate-spin" />;
    default:
      return <AlertCircle className="w-3 h-3 text-gray-600" />;
  }
};

export const VouchersSection: React.FC<VouchersSectionProps> = ({ dateRange, page, limit = 3, onPageChange, dashboardFilters }) => {
  const dispatch = useAppDispatch();
  const { vouchers, loading, error, totalCount } = useAppSelector((state) => state.voucher);
  const navigate = useNavigate();
  const lobTimezone = useLobTimezone();
  
  const formatDateToLocalString = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeSearchTerm, setActiveSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<VoucherSortField>("createdAt");
  const [sortOrder, setSortOrder] = useState<SortOrder>("DESC");

  const handleSearch = () => {
    setActiveSearchTerm(searchTerm);
    if (onPageChange) onPageChange(1);
  };

  // Infinite scroll observer
  const observer = useRef<IntersectionObserver | null>(null);
  const lastVoucherElementRef = useCallback((node: HTMLDivElement | null) => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && !isLoadingMore && vouchers.length < totalCount) {
        setIsLoadingMore(true);
        if (onPageChange) {
          onPageChange(page + 1);
        }
      }
    });
    if (node) observer.current.observe(node);
  }, [loading, page, totalCount, vouchers.length, onPageChange, isLoadingMore]);

  // Fetch vouchers using dashboard filters
  useEffect(() => {
    const fromDate = formatDateToLocalString(dateRange[0].startDate);
    const toDate = formatDateToLocalString(dateRange[0].endDate);
    const f = dashboardFilters;

    const params: VoucherListParams = {
      limit,
      page,
      fromDate,
      toDate,
      append: page > 1,
    };
    if (f.dateFilterType) params.dateField = f.dateFilterType;
    if (f.selectedProgram.length > 0) params.program = f.selectedProgram;
    if (f.selectedDistributor.length > 0) params.distributor = f.selectedDistributor;
    if (f.selectedChannel.length > 0) params.channel = f.selectedChannel;
    if (f.selectedProcessor.length > 0) params.processor = f.selectedProcessor;
    if (f.selectedState.length > 0) params.state = f.selectedState;
    if (f.selectedRecon.length > 0) params.recon = f.selectedRecon;
    if (activeSearchTerm) params.search = activeSearchTerm;
    params.sortBy = sortBy;
    params.sortOrder = sortOrder;
    params.timezone = f.timezone;

    dispatch(fetchVouchers(params)).finally(() => {
      setIsLoadingMore(false);
    });
  }, [dispatch, dateRange, page, limit, dashboardFilters, activeSearchTerm, sortBy, sortOrder]);

  const handleExportToExcel = async () => {
    setIsExporting(true);
    try {
      const f = dashboardFilters;
      const baseParamsWithoutDateField: VoucherListParams = {
        limit: 1000,
        page: 1,
        fromDate: formatDateToLocalString(dateRange[0].startDate),
        toDate: formatDateToLocalString(dateRange[0].endDate),
        append: false,
      };
      if (f.selectedProgram.length > 0) baseParamsWithoutDateField.program = f.selectedProgram;
      if (f.selectedDistributor.length > 0) baseParamsWithoutDateField.distributor = f.selectedDistributor;
      if (f.selectedChannel.length > 0) baseParamsWithoutDateField.channel = f.selectedChannel;
      if (f.selectedProcessor.length > 0) baseParamsWithoutDateField.processor = f.selectedProcessor;
      if (f.selectedState.length > 0) baseParamsWithoutDateField.state = f.selectedState;
      if (f.selectedRecon.length > 0) baseParamsWithoutDateField.recon = f.selectedRecon;
      if (activeSearchTerm) baseParamsWithoutDateField.search = activeSearchTerm;
      baseParamsWithoutDateField.sortBy = sortBy;
      baseParamsWithoutDateField.sortOrder = sortOrder;
      baseParamsWithoutDateField.timezone = f.timezone;

      const fetchAllPagesForDateField = async (dateField: VoucherListParams["dateField"]) => {
        const firstParams: VoucherListParams = {
          ...baseParamsWithoutDateField,
          page: 1,
          append: false,
          dateField,
        };
        const firstData = await fetchVoucherListApi(firstParams);
        const totalRows = firstData.totalCount || 0;
        let mergedVouchers = [...firstData.vouchers];

        if (totalRows > 1000) {
          const totalPages = Math.ceil(totalRows / 1000);
          for (let page = 2; page <= totalPages; page++) {
            const pageParams: VoucherListParams = { ...firstParams, page, append: false };
            const pageData = await fetchVoucherListApi(pageParams);
            mergedVouchers = [...mergedVouchers, ...pageData.vouchers];
          }
        }

        return mergedVouchers;
      };

      const dateFieldsToExport: VoucherListParams["dateField"][] =
        f.dateFilterType === "createdAt"
          ? ["createdAt", "voucherCreateDate", "expiryDate"]
          : [f.dateFilterType];

      const vouchersByDateField = await Promise.all(
        dateFieldsToExport.map((dateField) => fetchAllPagesForDateField(dateField))
      );

      const allVouchers = vouchersByDateField.flat();

      // A voucher can appear in multiple date-field queries; keep unique vouchers in export.
      const uniqueVoucherMap = new Map<string, any>();
      allVouchers.forEach((voucher: any) => {
        const uniqueKey = voucher.voucherId || voucher.saralId || JSON.stringify(voucher);
        if (!uniqueVoucherMap.has(uniqueKey)) {
          uniqueVoucherMap.set(uniqueKey, voucher);
        }
      });
      const uniqueVouchers = Array.from(uniqueVoucherMap.values());
        
        // Prepare data for export
        const exportData = uniqueVouchers.map((voucher: any) => {
          const currentDate = new Date();
          const expiryDate = voucher.expiryDate ? new Date(voucher.expiryDate) : null;
          const isExpired = expiryDate ? expiryDate < currentDate : false;
          const distributorId = voucher.distributorId
            ?? voucher['voucher_request.distributorId']
            ?? voucher['voucher_request.distributor.distributorId']
            ?? null;
          const distributorName = voucher.distributorName
            ?? voucher['voucher_request.distributor.name']
            ?? null;

          return {
            'Voucher ID': voucher.voucherId,
            'SARAL ID': voucher.saralId,
            'Program Code': voucher.programCode,
            'Distributor ID': distributorId ?? '-',
            'Distributor Name': distributorName ?? '-',
            'Amount': parseFloat(voucher.amount) || 0,
            'State': voucher.state,
            'Expiry Date': formatDateInTimezone(voucher.expiryDate, f.timezone, lobTimezone.iana),
            'Expired': isExpired ? 'True' : 'False',
            'Created Date': formatDateInTimezone(voucher.createdAt, f.timezone, lobTimezone.iana),
            'Activated Date': formatDateInTimezone(voucher.voucherCreateDate, f.timezone, lobTimezone.iana),
            'Reference ID': voucher["voucher_request.referenceId"] || '-',
            'Recon Status': voucher.reconDisplayStatus || '-',
            'Code': voucher.code || '-',
            'Available Amount': parseFloat(voucher.availableAmount) || 0,
          };
        });

        // Create workbook and worksheet
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(exportData);

        // Generate filename with date range
        const fromDate = formatDateToLocalString(dateRange[0].startDate);
        const toDate = formatDateToLocalString(dateRange[0].endDate);
        const fileName = `vouchers_${fromDate}_${toDate}.xlsx`;

        // Add worksheet to workbook and download
        XLSX.utils.book_append_sheet(workbook, worksheet, "Vouchers");
        XLSX.writeFile(workbook, fileName);
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleVoucherInfoClick = (saralId: string) => {
    // Store the saralId in localStorage for ERupiVoucher to use
    localStorage.setItem('erupiVoucherSaralId', saralId);
    navigate("/erupi-voucher");
  };

  return (
    <div className="max-w-7xl mx-auto px-8ma sm:px-6 lg:px-8 mt-8 mb-8">
      <div className="bg-white rounded-xl  p-6 flex flex-col justify-center transition-shadow duration-200 transition-colors border-[1px] border-cardborder">
        <div className="flex justify-between items-center mb-6 px-1">
          <h3 className="font-semibold text-lg">Vouchers</h3>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value as VoucherSortField);
                if (onPageChange) onPageChange(1);
              }}
              className="px-3 py-2 border border-gray-200 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand"
            >
              {sortFieldOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  Sort: {option.label}
                </option>
              ))}
            </select>
            <select
              value={sortOrder}
              onChange={(e) => {
                setSortOrder(e.target.value as SortOrder);
                if (onPageChange) onPageChange(1);
              }}
              className="px-3 py-2 border border-gray-200 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand"
            >
              <option value="DESC">Newest first</option>
              <option value="ASC">Oldest first</option>
            </select>
            <div className="relative">
              <input
                type="text"
                placeholder="Search Reference ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                className="pl-4 pr-10 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
              />
              <button
                type="button"
                onClick={handleSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-md transition-colors"
              >
                <Search className="w-4 h-4 text-gray-400 hover:text-gray-600" />
              </button>
            </div>
            <button
              type="button"
              onClick={handleExportToExcel}
              disabled={isExporting}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isExporting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Exporting...</span>
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  <span>Export to Excel</span>
                </>
              )}
            </button>
          </div>
        </div>

        {loading && !isLoadingMore && (
          <div className="py-8 flex justify-center items-center gap-2">
            <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
            <span>Loading vouchers...</span>
          </div>
        )}
        {error && <div className="py-8 text-center text-red-500">{error}</div>}
        {!loading && !error && vouchers.length === 0 && (
          <div className="py-8 text-center text-gray-500">No vouchers found for the selected filters.</div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {vouchers.map((voucher, index) => (
            <div
              key={voucher.voucherId}
              ref={index === vouchers.length - 1 ? lastVoucherElementRef : null}
              className="bg-white rounded-xl hover:shadow-lg p-4 flex flex-col border border-cardborder transition-shadow duration-200"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Hash className="w-4 h-4 text-gray-400" />
                  <span className="text-xs text-gray-400 font-mono">{voucher.saralId}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleVoucherInfoClick(voucher.saralId)}
                    className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                    title="View voucher details"
                    aria-label="View voucher details"
                  >
                    <Info className="w-4 h-4 text-gray-500 hover:text-gray-700" strokeWidth={2} />
                  </button>
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${
                      statusColors[voucher.state?.toLowerCase()] || "bg-gray-100 text-gray-500"
                    }`}
                  >
                    {voucher.state}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2 mb-2 border-b border-gray-100 pb-2">
                <Banknote className="w-4 h-4 text-gray-900" />
                <div className="text-lg font-bold text-gray-900">{voucher.amount}</div>
                <div className="text-xs text-gray-500 ml-2">{voucher.programCode}</div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs text-gray-400 mt-2">
                {/* Expiry */}
                <div className="flex items-center gap-1">
                  <Calendar className="w-3 h-3 text-green-500" />
                  <span className="text-gray-400">Exp:</span>
                  <span className="text-gray-500">{voucher.expiryDate ? new Date(voucher.expiryDate).toLocaleDateString() : "-"}</span>
                </div>
                {/* Created */}
                <div className="flex items-center gap-1">
                  <Clock className="w-3 h-3 text-blue-500" />
                  <span className="text-gray-400">Created:</span>
                  <span className="text-gray-500">{voucher.createdAt ? new Date(voucher.createdAt).toLocaleDateString() : "-"}</span>
                </div>
                {/* Recon */}
                <div className="flex items-center gap-1 col-span-2 sm:col-span-1 mt-1">
                  <DollarSign className="w-3 h-3 text-orange-500" />
                  <span className="text-gray-400">Recon:</span>
                  <span className="text-gray-500">{voucher.reconDisplayStatus}</span>
                </div>
                {/* Activated (voucher create date from issuer/bank flow) */}
                <div className="flex items-center gap-1 col-span-2 sm:col-span-1 mt-1">
                  <CheckCircle className="w-3 h-3 text-emerald-500 shrink-0" />
                  <span className="text-gray-400">Activated:</span>
                  <span className="text-gray-500">
                    {voucher.voucherCreateDate
                      ? new Date(voucher.voucherCreateDate).toLocaleDateString()
                      : "-"}
                  </span>
                </div>
                {/* Reference — same grid, full-width row below */}
                <div className="flex flex-wrap items-start gap-x-1 gap-y-0.5 col-span-2 mt-1">
                  <Tag className="w-3 h-3 text-purple-500 shrink-0 mt-0.5" />
                  <span className="text-gray-400 shrink-0">Reference:</span>
                  <span className="text-gray-500 break-all min-w-0">
                    {voucher["voucher_request.referenceId"] || "-"}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
        {isLoadingMore && (
          <div className="flex justify-center items-center gap-2 mt-4 py-4">
            <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
            <span className="text-sm text-gray-500">Loading more vouchers...</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default VouchersSection; 
